import { useCallback, useEffect, useRef, useState } from "react";
import type { ITransactionImport, ITransactionImportError, TransactionImportType } from "@types";
import { createTransactionImport, getTransactionImport, getTransactionImportErrors } from "@services/form";
import { Notification } from "@rio-cloud/rio-uikit";
import { useAppSelector } from "@store";
import { getUserAccount } from "@store/login/LoginSlice";
import { useT } from "@hooks";
import { resolveImportResultMessage, resolveImportResultTone } from "./importMessages";
import { hasImportErrors, INITIAL_IMPORT_STATE, toImportRejection, TransactionImportState } from "./importState";

export const IMPORT_POLLING_INTERVAL_MS = 2000;
export const IMPORT_ERRORS_PAGE_SIZE = 500;
export const IMPORT_MAX_POLLING_FAILURES = 3;

interface Options {
  isDialogOpen?: boolean;
  onImported?: () => void;
  onFinished?: (data: ITransactionImport, errors: ITransactionImportError[]) => void;
}

const notifyImportResult = (tone: ReturnType<typeof resolveImportResultTone>, message: string) => {
  if (tone === "success") Notification.success(message);
  else if (tone === "warning") Notification.warning(message);
  else Notification.error(message);
};

export const useTransactionImport = (
  type: TransactionImportType,
  { isDialogOpen = false, onImported, onFinished }: Options = {}
) => {
  const t = useT();
  const accountId = useAppSelector(getUserAccount);
  const [state, setState] = useState<TransactionImportState>(INITIAL_IMPORT_STATE);
  const runRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const onImportedRef = useRef(onImported);
  onImportedRef.current = onImported;
  const isDialogOpenRef = useRef(isDialogOpen);
  isDialogOpenRef.current = isDialogOpen;
  const tRef = useRef(t);
  tRef.current = t;

  const stopPolling = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const loadErrors = useCallback(
    async (importId: string, run: number) => {
      const errors: ITransactionImportError[] = [];
      let page = 0;

      while (run === runRef.current) {
        const result = await getTransactionImportErrors(importId, page, IMPORT_ERRORS_PAGE_SIZE, accountId);
        errors.push(...result.content);
        page += 1;
        if (result.last || result.content.length === 0 || page >= result.totalPages) break;
      }

      return errors;
    },
    [accountId]
  );

  const finish = useCallback(
    async (data: ITransactionImport, run: number) => {
      let errors: ITransactionImportError[] = [];

      if (hasImportErrors(data)) {
        try {
          errors = await loadErrors(data.importId, run);
        } catch {
          errors = [];
        }
      }

      if (run !== runRef.current) return;

      const keepResult = isDialogOpenRef.current || hasImportErrors(data);

      setState((prev) =>
        keepResult ? { ...prev, phase: "finished", importData: data, errors } : INITIAL_IMPORT_STATE
      );

      if ((data.summary?.importedRows ?? 0) > 0) onImportedRef.current?.();

      if (!isDialogOpenRef.current) {
        notifyImportResult(resolveImportResultTone(data), resolveImportResultMessage(data, tRef.current));
      }

      onFinishedRef.current?.(data, errors);
    },
    [loadErrors]
  );

  const poll = useCallback(
    (importId: string, run: number, failures = 0) => {
      timerRef.current = setTimeout(async () => {
        try {
          const data = await getTransactionImport(importId, accountId);
          if (run !== runRef.current) return;

          if (data.status === "PROCESSING") {
            setState((prev) => ({ ...prev, importData: data }));
            poll(importId, run);
            return;
          }

          await finish(data, run);
        } catch (error) {
          if (run !== runRef.current) return;

          const rejection = toImportRejection(error);

          if (rejection.status === 404 || failures + 1 >= IMPORT_MAX_POLLING_FAILURES) {
            setState((prev) => ({
              ...prev,
              phase: "rejected",
              rejection: { ...rejection, code: rejection.code ?? "IMPORT_TRACKING_FAILED" },
            }));
            return;
          }

          poll(importId, run, failures + 1);
        }
      }, IMPORT_POLLING_INTERVAL_MS);
    },
    [accountId, finish]
  );

  const start = useCallback(
    async (file: File) => {
      stopPolling();
      const run = ++runRef.current;
      setState({ ...INITIAL_IMPORT_STATE, phase: "uploading", fileName: file.name });

      try {
        const data = await createTransactionImport(file, type, accountId);
        if (run !== runRef.current) return;

        if (data.status === "PROCESSING") {
          setState((prev) => ({ ...prev, phase: "processing", importData: data }));
          poll(data.importId, run);
          return;
        }

        await finish(data, run);
      } catch (error) {
        if (run !== runRef.current) return;
        setState((prev) => ({ ...prev, phase: "rejected", rejection: toImportRejection(error) }));
      }
    },
    [accountId, finish, poll, stopPolling, type]
  );

  const reset = useCallback(() => {
    stopPolling();
    runRef.current += 1;
    setState(INITIAL_IMPORT_STATE);
  }, [stopPolling]);

  useEffect(
    () => () => {
      runRef.current += 1;
      stopPolling();
    },
    [stopPolling]
  );

  return { state, start, reset };
};
